import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog.tsx';
import { Button } from '@/components/ui/button.tsx';
import { Input } from '@/components/ui/input.tsx';
import { Label } from '@/components/ui/label.tsx';
import { useCreateOrganization } from '@/hooks/useCreateOrganization.ts';

interface CreateOrganizationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Create-organization dialog, launched from the ContextSwitcher.
 *
 * The new org is owned by the current user. The reassurance copy matters here:
 * most people opening this were invited into someone else's org, so we make
 * explicit that creating their own doesn't sever that access.
 */
export function CreateOrganizationDialog({ open, onOpenChange }: CreateOrganizationDialogProps) {
  const [name, setName] = useState('');
  const createOrg = useCreateOrganization();

  const handleSubmit = (e: React.SubmitEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (trimmed.length < 2) return;
    createOrg.mutate(trimmed, {
      onSuccess: () => {
        setName('');
        onOpenChange(false);
      },
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!createOrg.isPending) {
          if (!next) setName('');
          onOpenChange(next);
        }
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create organization</DialogTitle>
          <DialogDescription>
            You'll own this organization. You'll keep access to any organizations
            you've been invited to.
          </DialogDescription>
        </DialogHeader>
        <form id="create-organization-form" onSubmit={handleSubmit} className="space-y-1.5">
          <Label htmlFor="create-org-name">Organization name</Label>
          <Input
            id="create-org-name"
            type="text"
            placeholder="Acme Corp"
            value={name}
            onChange={(e) => { setName(e.target.value); }}
            autoFocus
            required
            minLength={2}
            disabled={createOrg.isPending}
          />
        </form>
        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => { onOpenChange(false); }}
            disabled={createOrg.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="create-organization-form"
            disabled={createOrg.isPending || name.trim().length < 2}
          >
            {createOrg.isPending ? 'Creating…' : 'Create organization'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
